/**
 * LineItemInline
 *
 * Desktop-native version of LineItemModal — same fields and logic but
 * renders inline inside a card (no IonModal wrapper).
 */
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { IonIcon, IonSpinner } from '@ionic/react';
import { chevronDownOutline } from 'ionicons/icons';
import type { ZohoItem, ZohoTax, ZohoPricelist } from '../services/zohoServiceOrderService';
import type { ServiceOrderLineItem } from './ServiceOrderModal';
import { getPricelistInfo } from '@/utils/pricelists';
import styles from './LineItemInline.module.scss';
import { useTranslation } from 'react-i18next';

interface LineItemInlineProps {
  onSave: (item: ServiceOrderLineItem, addAnother: boolean) => void;
  onCancel: () => void;
  initial?: ServiceOrderLineItem | null;
  zohoItems: ZohoItem[];
  zohoTaxes: ZohoTax[];
  loadingItems?: boolean;
  loadingTaxes?: boolean;
  pricelists?: ZohoPricelist[];
  pricelistId?: string;
  onPricelistChange?: (id: string) => void;
  loadingPricelists?: boolean;
  isInclusiveTax?: boolean;
}

function emptyDraft(): ServiceOrderLineItem {
  return {
    name:         '',
    description:  '',
    quantity:     1,
    rate:         0,
    discount:     undefined,
    discountType: '%',
    tax_id:       undefined,
    tax_name:     undefined,
  };
}

const LineItemInline: React.FC<LineItemInlineProps> = ({
  onSave,
  onCancel,
  initial,
  zohoItems,
  zohoTaxes,
  loadingItems      = false,
  loadingTaxes      = false,
  pricelists        = [],
  pricelistId       = '',
  onPricelistChange,
  loadingPricelists = false,
  isInclusiveTax    = true,
}) => {
  const { t: tr } = useTranslation();
  const [draft, setDraft]                         = useState<ServiceOrderLineItem>(emptyDraft());
  const [rateManuallyEdited, setRateManuallyEdited] = useState(false);

  // Item search combobox
  const [itemSearch, setItemSearch]               = useState('');
  const [itemDropOpen, setItemDropOpen]           = useState(false);
  const itemWrapRef                               = useRef<HTMLDivElement>(null);
  const itemListRef                               = useRef<HTMLDivElement>(null);

  // Pricelist dropdown
  const [plDropOpen, setPlDropOpen]               = useState(false);
  const [plDropPos, setPlDropPos]                 = useState({ top: 0, left: 0, width: 0 });
  const plTriggerRef                              = useRef<HTMLButtonElement>(null);
  const plListRef                                 = useRef<HTMLDivElement>(null);

  // Tax dropdown
  const [taxDropOpen, setTaxDropOpen]             = useState(false);
  const [taxDropPos, setTaxDropPos]               = useState({ top: 0, left: 0, width: 0 });
  const taxTriggerRef                             = useRef<HTMLButtonElement>(null);
  const taxListRef                                = useRef<HTMLDivElement>(null);

  // Re-initializes the draft when switching between add/edit — computed
  // during render (React's "adjust state when a dependency changes"
  // pattern) rather than as setState calls inside an effect.
  const [prevDraftInitInputs, setPrevDraftInitInputs] = useState({ initial, zohoTaxes });
  if (prevDraftInitInputs.initial !== initial || prevDraftInitInputs.zohoTaxes !== zohoTaxes) {
    setPrevDraftInitInputs({ initial, zohoTaxes });
    setRateManuallyEdited(false);
    if (initial) {
      setDraft({ ...initial });
    } else {
      const defaultTax =
        zohoTaxes.find(t => t.is_tax_group && t.tax_name.toLowerCase().includes('washington sales tax') && !t.tax_name.toLowerCase().includes('2025')) ??
        zohoTaxes.find(t => t.is_tax_group) ??
        zohoTaxes[0];
      setDraft({
        ...emptyDraft(),
        tax_id:   defaultTax?.tax_id   ?? undefined,
        tax_name: defaultTax?.tax_name ?? undefined,
      });
    }
  }

  // Refresh rate when pricelist changes (unless user manually typed a
  // rate) — computed during render for the same reason as above.
  const [prevZohoItemsForRate, setPrevZohoItemsForRate] = useState(zohoItems);
  if (zohoItems !== prevZohoItemsForRate) {
    setPrevZohoItemsForRate(zohoItems);
    if (draft.item_id && !rateManuallyEdited) {
      const zi = zohoItems.find(z => z.item_id === draft.item_id);
      if (zi && zi.rate !== draft.rate) setDraft(prev => ({ ...prev, rate: zi.rate }));
    }
  }

  useEffect(() => {
    if (!plDropOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!plTriggerRef.current?.contains(t) && !plListRef.current?.contains(t)) setPlDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [plDropOpen]);

  useEffect(() => {
    if (!taxDropOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!taxTriggerRef.current?.contains(t) && !taxListRef.current?.contains(t)) setTaxDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [taxDropOpen]);

  const set = <K extends keyof ServiceOrderLineItem>(key: K, value: ServiceOrderLineItem[K]) =>
    setDraft(prev => ({ ...prev, [key]: value }));

  const canSave = draft.name.trim().length > 0 && !!pricelistId;

  const handleSave = (addAnother: boolean) => {
    if (!canSave) return;
    onSave({ ...draft }, addAnother);
    if (addAnother) setDraft(emptyDraft());
  };

  const lineAmount     = draft.quantity * draft.rate;
  const selectedTaxPct = zohoTaxes.find(t => t.tax_id === draft.tax_id)?.tax_percentage ?? 0;
  const netAmount = (() => {
    if (!draft.discount) return lineAmount;
    if (!isInclusiveTax || draft.discountType !== '$') {
      const d = draft.discountType === '%' ? lineAmount * (draft.discount / 100) : draft.discount;
      return lineAmount - d;
    }
    return lineAmount - draft.discount * (1 + selectedTaxPct / 100);
  })();
  const discountAmt  = lineAmount - netAmount;
  const selectedTax  = zohoTaxes.find(t => t.tax_id === draft.tax_id);

  return (
    <>
      <div className={styles.panel}>
        <div className={styles.panelTitle}>{initial ? tr('components.LineItemInline.editLineItem') : tr('components.LineItemInline.addLineItem')}</div>

        {/* Item */}
        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.LineItemInline.item')}</label>
          <div ref={itemWrapRef} className={styles.itemCombo}>
            {loadingItems
              ? <div className={styles.pickerBtn}><IonSpinner name="dots" style={{ width: 14, height: 14 }} />{tr('components.LineItemInline.loading')}</div>
              : <input
                  className={styles.input}
                  placeholder={tr('components.LineItemInline.searchItem')}
                  value={itemDropOpen ? itemSearch : (draft.name || '')}
                  onFocus={() => { setItemSearch(''); setItemDropOpen(true); }}
                  onBlur={() => setTimeout(() => setItemDropOpen(false), 150)}
                  onChange={e => { setItemSearch(e.target.value); setItemDropOpen(true); }}
                />
            }
            {itemDropOpen && (() => {
              // Per-word match (AND logic), not one contiguous substring —
              // "deep cleaning 3" doesn't appear as one block in "Deep
              // Cleaning | 3 Bed / 2 Bath...", each word has to be found
              // independently, in any order/position within the name.
              // Cap is much higher for an actual query than for the
              // empty/browse-all case — the empty case is what needed
              // capping to 20 in the first place (~1900 items with nothing
              // to filter on), but a real search naturally narrows the
              // catalog down to a far smaller, genuinely browsable set, so a
              // low cap there just hides real matches.
              const searchTokens = itemSearch.toLowerCase().split(/\s+/).filter(Boolean);
              const filtered = zohoItems.filter(zi => {
                const name = zi.name.toLowerCase();
                return searchTokens.every(t => name.includes(t));
              }).slice(0, searchTokens.length ? 100 : 20);
              return filtered.length > 0 ? (
                <div ref={itemListRef} className={styles.itemDropdown}>
                  {filtered.map(zi => (
                    <button key={zi.item_id} type="button" className={styles.itemDropdownRow}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => {
                        setRateManuallyEdited(false);
                        setDraft(prev => ({ ...prev, item_id: zi.item_id, name: zi.name, rate: zi.rate, description: zi.description ?? '' }));
                        setItemDropOpen(false);
                      }}>
                      <span className={styles.itemDropName}>{zi.name}</span>
                      {zi.rate > 0 && <span className={styles.itemDropRate}>${zi.rate.toFixed(2)}</span>}
                    </button>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
        </div>

        {/* Description */}
        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.LineItemInline.description')}</label>
          <textarea
            className={styles.textarea}
            rows={2}
            placeholder={tr('components.LineItemInline.addADescriptionForYourItem')}
            value={draft.description}
            onChange={e => set('description', e.target.value)}
          />
        </div>

        {/* Qty + Rate (two columns) */}
        <div className={styles.twoCol}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>{tr('components.LineItemInline.quantity')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={styles.input}
              value={draft.quantity}
              onChange={e => set('quantity', Number(e.target.value ?? 1))}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>{tr('components.LineItemInline.rate')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={styles.input}
              value={draft.rate}
              onChange={e => { set('rate', Number(e.target.value ?? 0)); setRateManuallyEdited(true); }}
            />
          </div>
        </div>

        {/* Price List */}
        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.LineItemInline.priceList')}</label>
          <button
            ref={plTriggerRef}
            type="button"
            className={styles.pickerBtn}
            disabled={loadingPricelists}
            title={pricelistId ? getPricelistInfo(pricelists.find(p => p.pricelist_id === pricelistId)?.name ?? '').tip : undefined}
            onClick={() => {
              if (plTriggerRef.current) {
                const r = plTriggerRef.current.getBoundingClientRect();
                setPlDropPos({ top: r.bottom - 2, left: r.left, width: r.width });
              }
              setPlDropOpen(v => !v);
            }}
          >
            <span className={!pricelistId ? styles.placeholder : undefined}>
              {loadingPricelists ? tr('components.LineItemInline.loading') : pricelistId ? (getPricelistInfo(pricelists.find(p => p.pricelist_id === pricelistId)?.name ?? '').name) : tr('components.LineItemInline.selectPriceList')}
            </span>
            <IonIcon icon={chevronDownOutline} className={`${styles.pickerIcon} ${plDropOpen ? styles.caretOpen : ''}`} />
          </button>
          {plDropOpen && ReactDOM.createPortal(
            <div ref={plListRef} className={styles.customDropdownList}
              style={{ top: plDropPos.top, left: plDropPos.left, width: plDropPos.width }}>
              <button type="button" className={styles.customDropdownItem}
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onPricelistChange?.(''); setPlDropOpen(false); }}>{tr('components.LineItemInline.none')}
              </button>
              {pricelists.map(p => {
                const { name, tip } = getPricelistInfo(p.name);
                return (
                  <button key={p.pricelist_id} type="button"
                    className={`${styles.customDropdownItem} ${pricelistId === p.pricelist_id ? styles.customDropdownItemActive : ''}`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { onPricelistChange?.(p.pricelist_id); setPlDropOpen(false); }}
                    title={tip}>
                    {name}{p.currency_code ? ` (${p.currency_code})` : ''}
                  </button>
                );
              })}
            </div>,
            document.body
          )}
        </div>

        {/* Discount */}
        <div className={styles.twoCol}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>{tr('components.LineItemInline.discount')}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={styles.input}
              placeholder="0"
              value={draft.discount ?? ''}
              onChange={e => {
                const v = e.target.value;
                set('discount', v === '' ? undefined : Number(v));
              }}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>{tr('components.LineItemInline.discountType')}</label>
            <div className={styles.radioRow}>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="lineItemDiscount"
                  checked={draft.discountType === '%'}
                  onChange={() => set('discountType', '%')}
                />
                %
              </label>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="lineItemDiscount"
                  checked={draft.discountType === '$'}
                  onChange={() => set('discountType', '$')}
                />
                $
              </label>
            </div>
          </div>
        </div>

        {/* Tax */}
        <div className={styles.field}>
          <label className={styles.fieldLabel}>{tr('components.LineItemInline.tax')}</label>
          <button
            ref={taxTriggerRef}
            type="button"
            className={styles.pickerBtn}
            disabled={loadingTaxes}
            onClick={() => {
              if (taxTriggerRef.current) {
                const r = taxTriggerRef.current.getBoundingClientRect();
                setTaxDropPos({ top: r.bottom - 2, left: r.left, width: r.width });
              }
              setTaxDropOpen(v => !v);
            }}
          >
            <span className={draft.tax_id === undefined ? styles.placeholder : undefined}>
              {loadingTaxes ? tr('components.LineItemInline.loading') : selectedTax ? `${selectedTax.tax_name} (${selectedTax.tax_percentage}%)` : draft.tax_id === '' ? tr('components.LineItemInline.nonTaxable') : tr('components.LineItemInline.selectATax')}
            </span>
            <IonIcon icon={chevronDownOutline} className={`${styles.pickerIcon} ${taxDropOpen ? styles.caretOpen : ''}`} />
          </button>
          {taxDropOpen && ReactDOM.createPortal(
            <div ref={taxListRef} className={styles.customDropdownList}
              style={{ top: taxDropPos.top, left: taxDropPos.left, width: taxDropPos.width }}>
              <button type="button" className={styles.customDropdownItem}
                onMouseDown={e => e.preventDefault()}
                onClick={() => { setDraft(prev => ({ ...prev, tax_id: '', tax_name: '' })); setTaxDropOpen(false); }}>{tr('components.LineItemInline.nonTaxable')}
              </button>
              {zohoTaxes.filter(t => !t.is_tax_group).map(t => (
                <button key={t.tax_id} type="button"
                  className={`${styles.customDropdownItem} ${draft.tax_id === t.tax_id ? styles.customDropdownItemActive : ''}`}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { setDraft(prev => ({ ...prev, tax_id: t.tax_id, tax_name: t.tax_name })); setTaxDropOpen(false); }}>
                  {t.tax_name} ({t.tax_percentage}%)
                </button>
              ))}
              {zohoTaxes.filter(t => t.is_tax_group).map(t => (
                <button key={t.tax_id} type="button"
                  className={`${styles.customDropdownItem} ${draft.tax_id === t.tax_id ? styles.customDropdownItemActive : ''}`}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { setDraft(prev => ({ ...prev, tax_id: t.tax_id, tax_name: t.tax_name })); setTaxDropOpen(false); }}>{tr('components.LineItemInline.group', { tax_name: t.tax_name, tax_percentage: t.tax_percentage })}</button>
              ))}
            </div>,
            document.body
          )}
        </div>

        {/* Amount summary */}
        {draft.name.trim() && (
          <div className={styles.amountSummary}>
            <span className={styles.amountLabel}>{tr('components.LineItemInline.amount')}</span>
            <span className={styles.amountVal}>${netAmount.toFixed(2)}</span>
            {discountAmt > 0 && (
              <span className={styles.discountBadge}>−${discountAmt.toFixed(2)}</span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onCancel}>{tr('components.LineItemInline.cancel')}</button>
          {!initial && (
            <button className={styles.saveNewBtn} disabled={!canSave} onClick={() => handleSave(true)}>{tr('components.LineItemInline.saveNew')}
            </button>
          )}
          <button className={styles.saveBtn} disabled={!canSave} onClick={() => handleSave(false)}>{tr('components.LineItemInline.save')}
          </button>
        </div>
      </div>

     
    </>
  );
};

export default LineItemInline;
